/**
 * Política de cancelación de Mostrando México.
 * ESTADO: APROBADA por el titular el 2026-10-22.
 * La versión se guarda en la reserva y en los metadatos de Stripe como
 * evidencia de lo aceptado. Cambiar el texto exige subir la versión.
 */
export const CANCELLATION_POLICY_VERSION = "cancel-2026-10-22";

type Lang = "es" | "en" | "fr" | "de" | "it" | "pt";

export const CANCELLATION_POLICY: Record<Lang, string[]> = {
  es: [
    "Cambio de fecha gratis hasta 24 h antes, sujeto a disponibilidad.",
    "Cancelación con más de 24 h: reembolso del total menos 5% de cargo administrativo.",
    "Con menos de 24 h o no presentarse: sin reembolso.",
    "Cancelaciones por clima o por el operador: reembolso total o cambio de fecha.",
  ],
  en: [
    "Free date change up to 24 h before, subject to availability.",
    "Cancellation more than 24 h before: full refund minus a 5% administrative fee.",
    "Less than 24 h before or no-show: no refund.",
    "Cancellations due to weather or by the operator: full refund or date change.",
  ],
  fr: [
    "Changement de date gratuit jusqu'à 24 h avant, selon disponibilité.",
    "Annulation plus de 24 h avant : remboursement total moins 5 % de frais administratifs.",
    "Moins de 24 h avant ou absence : aucun remboursement.",
    "Annulation pour météo ou par l'opérateur : remboursement total ou changement de date.",
  ],
  de: [
    "Kostenlose Terminänderung bis 24 Std. vorher, je nach Verfügbarkeit.",
    "Stornierung mehr als 24 Std. vorher: volle Erstattung abzüglich 5 % Bearbeitungsgebühr.",
    "Weniger als 24 Std. vorher oder Nichterscheinen: keine Erstattung.",
    "Stornierung wegen Wetter oder durch den Anbieter: volle Erstattung oder Terminänderung.",
  ],
  it: [
    "Cambio data gratuito fino a 24 h prima, secondo disponibilità.",
    "Cancellazione oltre 24 h prima: rimborso totale meno il 5% di costi amministrativi.",
    "Meno di 24 h prima o mancata presentazione: nessun rimborso.",
    "Cancellazioni per meteo o dell'operatore: rimborso totale o cambio data.",
  ],
  pt: [
    "Troca de data grátis até 24 h antes, sujeita à disponibilidade.",
    "Cancelamento com mais de 24 h: reembolso total menos 5% de taxa administrativa.",
    "Com menos de 24 h ou não comparecimento: sem reembolso.",
    "Cancelamentos por clima ou pelo operador: reembolso total ou troca de data.",
  ],
};

export const CANCELLATION_UI: Record<Lang, { title: string; accept: string; required: string }> = {
  es: { title: "Política de cancelación", accept: "Acepto la política de cancelación", required: "Acepta la política de cancelación para continuar." },
  en: { title: "Cancellation policy", accept: "I accept the cancellation policy", required: "Accept the cancellation policy to continue." },
  fr: { title: "Conditions d'annulation", accept: "J'accepte les conditions d'annulation", required: "Acceptez les conditions d'annulation pour continuer." },
  de: { title: "Stornobedingungen", accept: "Ich akzeptiere die Stornobedingungen", required: "Bitte akzeptiere die Stornobedingungen, um fortzufahren." },
  it: { title: "Politica di cancellazione", accept: "Accetto la politica di cancellazione", required: "Accetta la politica di cancellazione per continuare." },
  pt: { title: "Política de cancelamento", accept: "Aceito a política de cancelamento", required: "Aceite a política de cancelamento para continuar." },
};

function k(lang: string | undefined): Lang {
  const l = (lang ?? "es").slice(0, 2) as Lang;
  return l in CANCELLATION_POLICY ? l : "es";
}

export function cancellationPolicy(lang: string | undefined): string[] {
  return CANCELLATION_POLICY[k(lang)];
}

export function cancellationUi(lang: string | undefined) {
  return CANCELLATION_UI[k(lang)];
}

/** El servidor solo acepta la versión vigente. */
export function isAcceptedPolicyVersion(v: unknown): boolean {
  return v === CANCELLATION_POLICY_VERSION;
}

const RECOVERY_CONSENT: Record<Lang, string> = {
  es: "Si no termino el pago, envíenme un solo recordatorio por correo.",
  en: "If I don't finish paying, send me a single email reminder.",
  fr: "Si je ne termine pas le paiement, envoyez-moi un seul rappel par e-mail.",
  de: "Falls ich nicht fertig bezahle, schickt mir eine einzige Erinnerung per E-Mail.",
  it: "Se non completo il pagamento, inviatemi un solo promemoria via email.",
  pt: "Se eu não concluir o pagamento, enviem um único lembrete por e-mail.",
};

export function recoveryConsentLabel(lang: string | undefined): string {
  return RECOVERY_CONSENT[k(lang)];
}

/**
 * Política mostrada para un tour: la nuestra, salvo que Bókun dé al cliente
 * reembolso total hasta N h antes; entonces se aplica lo más flexible de ambas.
 */
const FLEX: Record<Lang, { free: (h: number) => string; mid: (h: number) => string; none: (h: number) => string }> = {
  es: { free: (h) => `Cancelación gratuita hasta ${h} h antes: reembolso total.`, mid: (h) => `Entre ${h} h y 24 h antes: reembolso del total menos 5% de cargo administrativo.`, none: (h) => `Con menos de ${h} h o no presentarse: sin reembolso.` },
  en: { free: (h) => `Free cancellation up to ${h} h before: full refund.`, mid: (h) => `Between ${h} h and 24 h before: full refund minus a 5% administrative fee.`, none: (h) => `Less than ${h} h before or no-show: no refund.` },
  fr: { free: (h) => `Annulation gratuite jusqu'à ${h} h avant : remboursement total.`, mid: (h) => `Entre ${h} h et 24 h avant : remboursement total moins 5 % de frais administratifs.`, none: (h) => `Moins de ${h} h avant ou absence : aucun remboursement.` },
  de: { free: (h) => `Kostenlose Stornierung bis ${h} Std. vorher: volle Erstattung.`, mid: (h) => `Zwischen ${h} Std. und 24 Std. vorher: volle Erstattung abzüglich 5 % Bearbeitungsgebühr.`, none: (h) => `Weniger als ${h} Std. vorher oder Nichterscheinen: keine Erstattung.` },
  it: { free: (h) => `Cancellazione gratuita fino a ${h} h prima: rimborso totale.`, mid: (h) => `Tra ${h} h e 24 h prima: rimborso totale meno il 5% di costi amministrativi.`, none: (h) => `Meno di ${h} h prima o mancata presentazione: nessun rimborso.` },
  pt: { free: (h) => `Cancelamento gratuito até ${h} h antes: reembolso total.`, mid: (h) => `Entre ${h} h e 24 h antes: reembolso total menos 5% de taxa administrativa.`, none: (h) => `Com menos de ${h} h ou não comparecimento: sem reembolso.` },
};

export function effectiveCancellationPolicy(lang: string | undefined, bokunFreeHours?: number | null): string[] {
  const l = k(lang);
  const base = CANCELLATION_POLICY[l];
  const h = typeof bokunFreeHours === "number" && Number.isFinite(bokunFreeHours) && bokunFreeHours > 0 ? Math.round(bokunFreeHours) : null;
  if (h == null) return base;
  const f = FLEX[l];
  if (h > 24) return [base[0], f.free(h), f.mid(h), base[2], base[3]];
  return [base[0], f.free(h), f.none(h), base[3]];
}
